import { FC, ReactNode, useCallback } from "react";
import { Navigate, useLocation, useSearchParams } from "react-router-dom";
import { useSelector } from "react-redux";
import { selectAuthState } from "@web-src/modules/auth/store/authSlice";
import { PageRoute } from "@web-src/modules/navigation/types";
import {
  SubscriptionPlanModuleId,
  useRestApiProvider,
  UserRole,
} from "@jugl-web/rest-api";
import { useEntityProvider } from "@web-src/modules/entities/providers/EntityProvider/useEntityProvider";
import { FullPageError } from "@web-src/modules/common/components/FullPageError";
import { PageLoaderFull, EmptyListContent } from "@jugl-web/ui-kit";
import { isPageUnloading } from "@jugl-web/utils";
import { usePrevious } from "@web-src/utils/helper";
import { AnonymousSessionGateway } from "@web-src/modules/auth/components/AnonymousSessionGateway";
import { getLoginFlow } from "@jugl-web/utils/hooks/useLoginFlow";
import { useShopifySubscriptionGate } from "@web-src/modules/shopify/hooks/useShopifySubscriptionGate";
import { useMe } from "@web-src/modules/auth/hooks/useMe";
import { useNavigateToRoleBasedInitialPage } from "../../hooks/useNavigateToRoleBasedInitialPage";
import { NavigateToDefaultEntity } from "../NavigateToDefaultEntity/NavigateToDefaultEntity";

interface PrivateRouteProps {
  /** Page content to render once all route guards pass. */
  component: ReactNode;
  /**
   * When true, the user must have a completed profile; otherwise they
   * are redirected to onboarding / profile form.
   * @default true
   */
  profileRequired?: boolean;
  /**
   * When true, a workspace must be selected; otherwise entity-scoped checks
   * are skipped.
   * @default true
   */
  entityRequired?: boolean;
  /**
   * When true, V1 `planIsActive` is required; otherwise inactive subscriptions
   * may still access the route (no redirect to the no-subscription page).
   * @default true
   */
  activeSubscriptionRequired?: boolean;
  /**
   * When true, unauthenticated visitors may view the page via an anonymous
   * session; otherwise they must sign in.
   * @default false
   */
  anonymousAllowed?: boolean;
  /** Allowed org-workspace roles; users outside this list are sent to their home page. */
  requiredUserRoles?: UserRole[];
  /** Allowed CX-workspace roles; users outside this list are sent to their home page. */
  requiredCXUserRoles?: UserRole[];
  /**
   * Subscription plan modules that must be available on the workspace; otherwise
   * a simple "Page not found" empty state is shown.
   */
  requiredModules?: SubscriptionPlanModuleId[];
}

/**
 * Auth / profile / entity / subscription gate for private app routes.
 * Evaluates guards in order and either redirects, shows a loader/error, or
 * eventually renders the provided `component`.
 */
const PrivateRoute: FC<PrivateRouteProps> = ({
  component,
  profileRequired = true,
  entityRequired = true,
  activeSubscriptionRequired = true,
  anonymousAllowed = false,
  requiredUserRoles,
  requiredModules,
  requiredCXUserRoles,
}) => {
  const { isAuthenticated } = useSelector(selectAuthState);
  const previousIsAuthenticated = usePrevious(isAuthenticated);

  const location = useLocation();
  const [searchParams] = useSearchParams();

  const linkToOpenAfterLogin = searchParams.get("next");

  const {
    isInitialized: isEntityInitialized,
    entity,
    isEntitiesLoading,
    reinitialize,
    isInitializationError,
    subscriptionInfo,
    isModuleAvailable,
  } = useEntityProvider();

  const { usersApi } = useRestApiProvider();

  const { me, isLoading: isMeLoading } = useMe();

  const {
    data: profileData,
    error: profileError,
    isLoading: isProfileLoading,
    isError: isProfileLoadingError,
    refetch: refetchProfileData,
  } = usersApi.useGetUserProfileQuery(
    { meta: { silent: true } },
    {
      skip: !isAuthenticated,
      refetchOnMountOrArgChange: false,
    }
  );

  const hasProfile = !!profileData;

  const handleRetryOnError = useCallback(async () => {
    if (isInitializationError) {
      reinitialize();
    }
    if (isProfileLoadingError) {
      refetchProfileData();
    }
  }, [
    isInitializationError,
    isProfileLoadingError,
    refetchProfileData,
    reinitialize,
  ]);

  const navigateToRoleBasedInitialPage = useNavigateToRoleBasedInitialPage();
  const shopifySubscriptionGate = useShopifySubscriptionGate();

  const isInvitationsPage = location.pathname === "/invitations";
  const isShopifyNoSubscriptionPage = location.pathname.endsWith(
    `/${PageRoute.shopifyNoSubscription}`
  );

  // Anonymous guest access — skip auth entirely when the route allows it
  if (!isAuthenticated && anonymousAllowed) {
    return <AnonymousSessionGateway>{component}</AnonymousSessionGateway>;
  }

  // Boot / first entities list load only (invitations page is exempt so users
  // can land there before a workspace is chosen). Background refetches from
  // live-update invalidation must not remount the whole app.
  if (
    (isEntitiesLoading && !isInvitationsPage) ||
    isAuthenticated === undefined
  ) {
    return <PageLoaderFull />;
  }

  // A hard navigation is in flight: the browser is aborting pending requests, so their
  // failures are not real. Hold the loader until the new document takes over.
  if (isPageUnloading()) {
    return <PageLoaderFull />;
  }

  // Hard failures: entity init failed, or profile fetch failed for a reason
  // other than "no profile yet" (404 means onboarding is still needed)
  if (
    (profileRequired &&
      isProfileLoadingError &&
      (profileError as unknown as { status?: number }).status !== 404) ||
    isInitializationError
  ) {
    return <FullPageError onRetry={handleRetryOnError} />;
  }

  // First paint while logged out — send to login and preserve the requested URL
  // as `?next=` (unless `next` already points at this same path)
  if (previousIsAuthenticated === undefined && isAuthenticated === false) {
    const requestedPath = `${location.pathname}${location.search}`;
    let next = "";
    if (linkToOpenAfterLogin !== requestedPath) {
      next = requestedPath;
    }
    return (
      <Navigate
        to={{
          pathname: `/${PageRoute.login}`,
          search: next ? `?next=${encodeURIComponent(next)}` : "",
        }}
      />
    );
  }

  // Still waiting on auth, profile, "me", or entity selection before we can
  // decide the next redirect / render
  if (
    !isAuthenticated ||
    (profileRequired && isProfileLoading && !hasProfile) ||
    (!me && isMeLoading) ||
    (entityRequired && !isEntityInitialized) ||
    (entityRequired && !entity && isEntitiesLoading)
  ) {
    return <PageLoaderFull />;
  }

  // Post-login deep link — finish the `?next=` redirect now that auth is ready
  if (linkToOpenAfterLogin) {
    return <Navigate to={linkToOpenAfterLogin} />;
  }

  // Authenticated but missing a profile — push into the correct onboarding flow
  if (isAuthenticated && !hasProfile && profileRequired) {
    if (getLoginFlow() === "cx") {
      return <Navigate to="/cx/onboarding" />;
    }

    return (
      <Navigate
        to={{
          pathname: `/${PageRoute.profileForm}`,
        }}
      />
    );
  }

  // Entity-scoped route — apply workspace, subscription, role, and module checks
  if (isAuthenticated && entityRequired) {
    // No workspace selected yet — pick / create a default one
    if (!entity) {
      return <NavigateToDefaultEntity />;
    }

    const hasActiveSubscription =
      !!subscriptionInfo && subscriptionInfo.planIsActive;

    // V1 subscription inactive — block with the dedicated no-subscription page
    if (activeSubscriptionRequired && !hasActiveSubscription) {
      return (
        <Navigate
          to={{ pathname: `/${entity.id}/${PageRoute.noSubscription}` }}
        />
      );
    }

    // Role gate — CX and org workspaces use separate allow-lists
    if (entity.type === "cx" && requiredCXUserRoles) {
      if (!requiredCXUserRoles.includes(entity.role)) {
        navigateToRoleBasedInitialPage(entity);
        return null;
      }
    } else if (requiredUserRoles && !requiredUserRoles.includes(entity.role)) {
      navigateToRoleBasedInitialPage(entity);
      return null;
    }

    // Plan module gate — required feature(s) not included in the current plan
    if (
      requiredModules &&
      !requiredModules.every((module) => isModuleAvailable(module))
    ) {
      return (
        <EmptyListContent
          type="error"
          customTitle="Page not found"
          customSubtitle="Please try something else"
          customButton={{
            text: "Go home",
            onClick: () => navigateToRoleBasedInitialPage(entity),
          }}
        />
      );
    }

    // Shopify billing incomplete — wait for subscription load, then redirect
    // to the dedicated no-subscription page (unless already there)
    if (shopifySubscriptionGate.status === "loading") {
      return <PageLoaderFull variant="content" />;
    }

    if (
      shopifySubscriptionGate.status === "blocked" &&
      !isShopifyNoSubscriptionPage
    ) {
      return (
        <Navigate
          to={{ pathname: `/${entity.id}/${PageRoute.shopifyNoSubscription}` }}
          replace
        />
      );
    }

    return <>{component}</>;
  }

  // Non-entity private route (e.g. invitations / global settings) — render as-is
  return <>{component}</>;
};

export default PrivateRoute;
